// Envíos > Lista legacy: tabla operativa de solo lectura sobre
// legacy.envios (la fuente que puebla el poller de Lightdata). Deliberadamente
// simple — sin dashboard, sin cards, sin métricas — solo una tabla paginada,
// ordenable y filtrable, parecida a la tabla legacy de referencia.
//
// No toca ni reemplaza la Lista de envíos actual (Deliveries.tsx,
// public.deliveries): es una fuente y una pantalla completamente aparte.
import React, { useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import EnvioLegacyDetailModal from "../components/envios/EnvioLegacyDetailModal";

type EnvioLegacyRow = {
  lightdata_id: number | string | null;
  numero_tracking: string | null;
  nombre_fantasia: string | null;
  id_venta_ml: string | null;
  origen: string | null;
  fecha_venta: string | null;
  fecha_estado: string | null;
  fecha_estado_ar: string | null;
  nombre_destinatario: string | null;
  cp: string | null;
  zona_entrega: string | null;
  zonas_costos: string | null;
  cadete: string | null;
  estado: string | null;
  qr_svg: string | null;
};

type SortDir = "asc" | "desc";

// "Hoy" operativo en hora Argentina (ítem 6/7) — nunca Date local del
// browser: un usuario fuera de Argentina tendría un "hoy" distinto al
// "hoy" que ya usa el backend (fecha_colecta — ver auditoría "LYNX —
// LightData vs Lista Legacy": el filtro Desde/Hasta usa fecha_colecta,
// no fecha_estado, para representar el mismo universo que LightData al
// consultar por Fecha Colecta — timezone-aware vía
// performance.periods.js). No hay ningún date-util compartido en src/ hoy
// (se buscó: ningún otro archivo en src/ usa America/Argentina/Buenos_Aires
// ni Intl.DateTimeFormat con timeZone), así que este es el primer uso de
// este patrón en el frontend — formato "en-CA" porque ya devuelve
// YYYY-MM-DD, el mismo formato que esperan los inputs date y el backend
// (isValidDateKey).
const ARGENTINA_TZ = "America/Argentina/Buenos_Aires";

function getArgentinaDateKey(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: ARGENTINA_TZ }).format(new Date());
}

// "hoy AR" ± N días calendario, calculado sobre medianoche UTC del dateKey
// (nunca sumando milisegundos sobre un Date con hora real, que podría
// cruzar un DST/offset distinto y correr un día) — simple y determinístico
// porque acá solo nos importa el día civil, no la hora.
export function addDaysToDateKey(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const utcMidnight = new Date(Date.UTC(y, m - 1, d));
  utcMidnight.setUTCDate(utcMidnight.getUTCDate() + days);
  return utcMidnight.toISOString().slice(0, 10);
}

// Día de la semana (0=domingo..6=sábado) de un dateKey puro, vía medianoche
// UTC — mismo truco que addDaysToDateKey, nunca depende del TZ del proceso.
function dowOfDateKey(dateKey: string): number {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

// Semana OPERATIVA WYN (ver auditoría "LYNX — Correcciones finales antes
// de cerrar FASE B", sección 2): viernes -> jueves, NUNCA "últimos 7 días"
// ni semana calendario lunes-domingo. Mismo algoritmo que ya usa el
// backend (fridayOfWeek en server/legacy/lightdata/lightdataJob.js:
// daysSinceFriday = (dow - 5 + 7) % 7) — reimplementado acá porque no hay
// ningún módulo de fechas compartido entre server/ y src/ en este repo
// (ver nota de getArgentinaDateKey arriba), nunca una definición nueva o
// distinta.
export function fridayOfOperationalWeek(dateKey: string): string {
  const daysSinceFriday = (dowOfDateKey(dateKey) - 5 + 7) % 7;
  return addDaysToDateKey(dateKey, -daysSinceFriday);
}

// weeksAgo=0 -> semana operativa actual (la que contiene `todayKey`);
// weeksAgo=1 -> la semana operativa inmediatamente anterior. `to` puede
// caer en el futuro respecto de "hoy" si todavía estamos a mitad de
// semana (ej. hoy lunes, la semana actual termina el jueves que viene) —
// es intencional: el filtro representa la semana operativa COMPLETA, y el
// backend ya tolera un `to` futuro sin problema (fecha_colecta simplemente
// no tiene filas más allá de "hoy" todavía, el WHERE no rompe nada).
export function resolveOperationalWeek(todayKey: string, weeksAgo = 0): { from: string; to: string } {
  const thisFriday = fridayOfOperationalWeek(todayKey);
  const friday = addDaysToDateKey(thisFriday, -7 * weeksAgo);
  const thursday = addDaysToDateKey(friday, 6);
  return { from: friday, to: thursday };
}

// 8b: se decidió multiselect por CATEGORÍA canónica (classifyOperationalStatus
// / STATUS_KEYS en performance.statusBreakdown.js), no por string crudo de
// `estado`. Motivo: el pedido original ("debe permitir, por ejemplo:
// Entregado + En camino + Reprogramado") describe categorías, no variantes
// de texto, y pedía explícitamente "normalización/clasificación correcta,
// no hacks de strings en frontend" — filtrar por categoría es lo único que
// garantiza que este filtro nunca diverge de esa clasificación (ver fix
// del bug en enviosList.repository.js, que usa la MISMA expresión SQL).
// Los valores (keys) son exactamente los STATUS_KEYS del backend; los
// labels son español, para la UI.
const ESTADO_CATEGORIES: { key: string; label: string }[] = [
  { key: "DELIVERED", label: "Entregado" },
  { key: "ABSENT", label: "Ausente" },
  { key: "RESCHEDULED", label: "Reprogramado" },
  { key: "DELAYED", label: "Demorado" },
  { key: "CANCELLED", label: "Cancelado" },
  { key: "REJECTED", label: "Rechazado" },
  { key: "IN_PROGRESS", label: "En camino" },
  { key: "UNKNOWN", label: "Sin clasificar" },
];

const FECHA_DESDE_INPUT_ID = "envios-legacy-fecha-desde";

type Column = {
  key: string;
  label: string;
  sortable: boolean;
  render: (row: EnvioLegacyRow) => React.ReactNode;
};

const PAGE_SIZE = 50;

const pageWrapStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "16px" };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "26px", fontWeight: 800, color: "var(--text-primary)", letterSpacing: "-0.02em" };
const subtitleStyle: React.CSSProperties = { margin: "6px 0 0 0", color: "var(--text-secondary)", fontSize: "14px" };
const filtersRowStyle: React.CSSProperties = { display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" };
const filterFieldStyle: React.CSSProperties = { minWidth: "160px", flex: "1 1 160px" };
const searchFieldStyle: React.CSSProperties = { minWidth: "240px", flex: "2 1 240px" };
const tableWrapStyle: React.CSSProperties = { overflowX: "auto", border: "1px solid var(--border)", borderRadius: "18px" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 10px",
  color: "var(--text-secondary)",
  fontSize: "12px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  cursor: "pointer",
  userSelect: "none",
  whiteSpace: "nowrap",
  background: "rgba(255,255,255,0.02)",
};
const thStaticStyle: React.CSSProperties = { ...thStyle, cursor: "default" };
const trClickableStyle: React.CSSProperties = { cursor: "pointer" };
const tdStyle: React.CSSProperties = {
  padding: "8px 10px",
  color: "var(--text-primary)",
  fontSize: "13px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};
const emptyStyle: React.CSSProperties = { padding: "24px 12px", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };
const footerRowStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" };
const pagerButtonStyle: React.CSSProperties = {
  padding: "6px 12px",
  borderRadius: "8px",
  border: "1px solid var(--border)",
  background: "transparent",
  color: "var(--text-primary)",
  cursor: "pointer",
  fontSize: "13px",
};
const pagerButtonDisabledStyle: React.CSSProperties = { ...pagerButtonStyle, opacity: 0.4, cursor: "not-allowed" };

// Ítem 7: botones de rango rápido. Mismo estilo base que los botones de
// paginación (pagerButtonStyle) para no introducir un patrón visual nuevo
// en una pantalla secundaria — "active" resalta el preset que coincide con
// el rango actualmente seleccionado (comparación simple de fechas, no hay
// estado separado para esto).
const quickRangeButtonStyle: React.CSSProperties = { ...pagerButtonStyle, fontWeight: 600 };
const quickRangeButtonActiveStyle: React.CSSProperties = {
  ...quickRangeButtonStyle,
  borderColor: "var(--accent, #58a6ff)",
  color: "var(--accent, #58a6ff)",
};
const rangeGroupStyle: React.CSSProperties = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  alignItems: "flex-end",
  border: "1px solid var(--border)",
  borderRadius: "14px",
  padding: "10px",
};
const rangeGroupLabelStyle: React.CSSProperties = {
  fontSize: "12px",
  fontWeight: 700,
  color: "var(--text-secondary)",
  width: "100%",
};

// Ítem 8b: panel de checkboxes de categoría de estado — mismo recuadro que
// el grupo "Rango" (rangeGroupStyle) para consistencia visual.
const estadoGroupStyle: React.CSSProperties = { ...rangeGroupStyle, flex: "2 1 320px" };
const estadoCheckboxLabelStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  fontSize: "13px",
  color: "var(--text-primary)",
  whiteSpace: "nowrap",
  cursor: "pointer",
};

const ESTADO_COLOR: Record<string, string> = {
  entregado: "var(--success, #2ea043)",
  cancelado: "var(--danger, #d1242f)",
  "no entregado": "var(--danger, #d1242f)",
  "rechazado por el comprador": "var(--danger, #d1242f)",
};

function estadoBadgeColor(estado: string | null): string {
  if (!estado) return "var(--text-secondary)";
  return ESTADO_COLOR[estado.trim().toLowerCase()] || "var(--warning, #d29922)";
}

const badgeStyle = (color: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "2px 9px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 600,
  border: `1px solid ${color}`,
  color,
  whiteSpace: "nowrap",
});

export default function EnviosLegacy() {
  const { token } = useAuth();

  const [items, setItems] = useState<EnvioLegacyRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [estadoCategories, setEstadoCategories] = useState<string[]>([]);
  const [cliente, setCliente] = useState("");
  const [chofer, setChofer] = useState("");
  const [origen, setOrigen] = useState("");
  // Ítem 6: la pantalla abre SIEMPRE mostrando "hoy" (Argentina) — antes
  // abría sin filtro de fecha (toda la tabla histórica paginada). from=to=
  // hoy en el primer request, nunca vacío.
  const [fechaDesde, setFechaDesde] = useState(() => getArgentinaDateKey());
  const [fechaHasta, setFechaHasta] = useState(() => getArgentinaDateKey());
  const [rangeError, setRangeError] = useState<string | null>(null);

  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const [selectedId, setSelectedId] = useState<number | string | null>(null);

  // Debounce simple: espera a que el usuario deje de tipear antes de
  // disparar la búsqueda contra el backend (legacy.envios no tiene índices
  // en las columnas de texto, así que no conviene una request por tecla).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!token) return;

    // Validación local antes de pegarle al backend — mismo criterio que
    // valida el servidor (ver enviosList.routes.js), evita un request que
    // sabemos de antemano que va a volver 400.
    if (fechaDesde && fechaHasta && fechaDesde > fechaHasta) {
      setRangeError("'Desde' no puede ser posterior a 'Hasta'.");
      setLoading(false);
      return;
    }
    setRangeError(null);

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (search.trim()) params.set("search", search.trim());
    if (estadoCategories.length) params.set("estado", estadoCategories.join(","));
    if (cliente.trim()) params.set("cliente", cliente.trim());
    if (chofer.trim()) params.set("chofer", chofer.trim());
    if (origen.trim()) params.set("origen", origen.trim());
    if (fechaDesde) params.set("from", fechaDesde);
    if (fechaHasta) params.set("to", fechaHasta);
    if (sortBy) {
      params.set("sortBy", sortBy);
      params.set("sortDir", sortDir);
    }

    authFetch(`/v1/envios-legacy?${params.toString()}`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar el listado legacy");
        if (cancelled) return;
        setItems(payload.items ?? []);
        setTotalPages(payload.totalPages ?? 1);
        setTotal(payload.total ?? 0);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, page, search, estadoCategories, cliente, chofer, origen, fechaDesde, fechaHasta, sortBy, sortDir]);

  const handleHeaderClick = (column: Column) => {
    if (!column.sortable) return;
    if (sortBy !== column.key) {
      setSortBy(column.key);
      setSortDir("desc");
    } else {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    }
    setPage(1);
  };

  // Ítem 8b: toggle de una categoría en el multiselect de estado — mismo
  // criterio que el resto de los filtros (resetea a página 1).
  const toggleEstadoCategory = (key: string) => {
    setEstadoCategories((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    setPage(1);
  };

  // Ítem 7: botones de rango rápido. Las tres primeras fijan fechaDesde/
  // fechaHasta y pasan por el mismo setFechaDesde/setFechaHasta que ya
  // dispara el fetch validado (no hay una segunda ruta de fetch). "Rango"
  // no toca las fechas: solo enfoca el input "Desde" para selección manual.
  const applyQuickRange = (desde: string, hasta: string) => {
    setFechaDesde(desde);
    setFechaHasta(hasta);
    setPage(1);
  };
  const today = getArgentinaDateKey();
  const yesterday = addDaysToDateKey(today, -1);
  // "Esta semana"/"Semana anterior" (ver auditoría "LYNX — Correcciones
  // finales antes de cerrar FASE B", sección 2): semana OPERATIVA WYN
  // (viernes->jueves), NUNCA "últimos 7 días" — corrección explícita sobre
  // una versión anterior que sí usaba una ventana de 7 días calendario.
  const thisWeek = resolveOperationalWeek(today);
  const previousWeek = resolveOperationalWeek(today, 1);
  const focusRangeInputs = () => {
    document.getElementById(FECHA_DESDE_INPUT_ID)?.focus();
  };

  const columns: Column[] = useMemo(
    () => [
      {
        key: "qr",
        label: "QR",
        sortable: false,
        render: (r) => (r.qr_svg ? <span style={{ width: 28, height: 28, display: "inline-block" }} dangerouslySetInnerHTML={{ __html: r.qr_svg }} /> : "—"),
      },
      { key: "nombre_fantasia", label: "Nombre fantasía", sortable: true, render: (r) => r.nombre_fantasia || "—" },
      { key: "id_venta_ml", label: "IDML", sortable: true, render: (r) => r.id_venta_ml || "—" },
      { key: "origen", label: "Origen", sortable: true, render: (r) => r.origen || "—" },
      { key: "numero_tracking", label: "Tracking number", sortable: true, render: (r) => r.numero_tracking || "—" },
      { key: "fecha_venta", label: "Fecha venta", sortable: true, render: (r) => r.fecha_venta || "—" },
      { key: "fecha_estado", label: "Fecha estado", sortable: true, render: (r) => r.fecha_estado_ar || "—" },
      { key: "nombre_destinatario", label: "Destino nombre", sortable: true, render: (r) => r.nombre_destinatario || "—" },
      { key: "cp", label: "Destino CP", sortable: true, render: (r) => r.cp || "—" },
      { key: "zona_entrega", label: "Zona entrega", sortable: true, render: (r) => r.zona_entrega || "—" },
      { key: "zonas_costos", label: "Zona costo", sortable: true, render: (r) => r.zonas_costos || "—" },
      { key: "cadete", label: "Chofer", sortable: true, render: (r) => r.cadete || "—" },
      {
        key: "estado",
        label: "Estado",
        sortable: true,
        render: (r) => (r.estado ? <span style={badgeStyle(estadoBadgeColor(r.estado))}>{r.estado}</span> : "—"),
      },
    ],
    []
  );

  return (
    <div style={pageWrapStyle}>
      <div>
        <h1 style={titleStyle}>Envíos — Lista legacy</h1>
        <p style={subtitleStyle}>
          Fuente: legacy.envios (Lightdata). Tabla operativa de solo lectura — no reemplaza la Lista de envíos actual.
        </p>
      </div>

      <Card padding="14px">
        <div style={filtersRowStyle}>
          <div style={searchFieldStyle}>
            <Input
              label="Buscar"
              placeholder="Tracking, cliente, destinatario, chofer…"
              value={searchInput}
              onChange={(e: any) => setSearchInput(e.target.value)}
            />
          </div>
          <div style={estadoGroupStyle}>
            <span style={rangeGroupLabelStyle}>Estado</span>
            {ESTADO_CATEGORIES.map((opt) => (
              <label key={opt.key} style={estadoCheckboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={estadoCategories.includes(opt.key)}
                  onChange={() => toggleEstadoCategory(opt.key)}
                />
                {opt.label}
              </label>
            ))}
          </div>
          <div style={filterFieldStyle}>
            <Input
              label="Cliente"
              placeholder="Nombre fantasía"
              value={cliente}
              onChange={(e: any) => { setCliente(e.target.value); setPage(1); }}
            />
          </div>
          <div style={filterFieldStyle}>
            <Input
              label="Chofer"
              placeholder="Cadete"
              value={chofer}
              onChange={(e: any) => { setChofer(e.target.value); setPage(1); }}
            />
          </div>
          <div style={filterFieldStyle}>
            <Input
              label="Origen"
              placeholder="MercadoLibre, TN, Directo…"
              value={origen}
              onChange={(e: any) => { setOrigen(e.target.value); setPage(1); }}
            />
          </div>
          <div style={rangeGroupStyle}>
            <span style={rangeGroupLabelStyle}>Rango</span>
            <button
              type="button"
              style={fechaDesde === today && fechaHasta === today ? quickRangeButtonActiveStyle : quickRangeButtonStyle}
              onClick={() => applyQuickRange(today, today)}
            >
              Hoy
            </button>
            <button
              type="button"
              style={fechaDesde === yesterday && fechaHasta === yesterday ? quickRangeButtonActiveStyle : quickRangeButtonStyle}
              onClick={() => applyQuickRange(yesterday, yesterday)}
            >
              Ayer
            </button>
            <button
              type="button"
              style={fechaDesde === thisWeek.from && fechaHasta === thisWeek.to ? quickRangeButtonActiveStyle : quickRangeButtonStyle}
              onClick={() => applyQuickRange(thisWeek.from, thisWeek.to)}
              title={`Semana operativa viernes->jueves: ${thisWeek.from} a ${thisWeek.to}`}
            >
              Esta semana
            </button>
            <button
              type="button"
              style={fechaDesde === previousWeek.from && fechaHasta === previousWeek.to ? quickRangeButtonActiveStyle : quickRangeButtonStyle}
              onClick={() => applyQuickRange(previousWeek.from, previousWeek.to)}
              title={`Semana operativa viernes->jueves: ${previousWeek.from} a ${previousWeek.to}`}
            >
              Semana anterior
            </button>
            <button type="button" style={quickRangeButtonStyle} onClick={focusRangeInputs}>
              Rango
            </button>
            <div style={filterFieldStyle}>
              <Input
                id={FECHA_DESDE_INPUT_ID}
                label="Desde"
                type="date"
                value={fechaDesde}
                onChange={(e: any) => { setFechaDesde(e.target.value); setPage(1); }}
              />
            </div>
            <div style={filterFieldStyle}>
              <Input
                label="Hasta"
                type="date"
                value={fechaHasta}
                onChange={(e: any) => { setFechaHasta(e.target.value); setPage(1); }}
              />
            </div>
          </div>
        </div>
      </Card>

      {rangeError ? (
        <Card padding="14px" style={{ color: "var(--danger, #d1242f)" }}>
          {rangeError}
        </Card>
      ) : null}

      {error ? (
        <Card padding="14px" style={{ color: "var(--danger, #d1242f)" }}>
          {error}
        </Card>
      ) : null}

      <Card padding="12px">
        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    style={column.sortable ? thStyle : thStaticStyle}
                    onClick={() => handleHeaderClick(column)}
                  >
                    {column.label}
                    {column.sortable && sortBy === column.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 && (
                <tr>
                  <td style={emptyStyle} colSpan={columns.length}>
                    {total === 0 ? "No hay envíos legacy sincronizados todavía." : "Nada para estos filtros."}
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td style={emptyStyle} colSpan={columns.length}>
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((row, index) => (
                  <tr
                    key={row.lightdata_id != null ? String(row.lightdata_id) : `${row.numero_tracking}-${index}`}
                    style={row.lightdata_id != null ? trClickableStyle : undefined}
                    onClick={() => row.lightdata_id != null && setSelectedId(row.lightdata_id)}
                    title={row.lightdata_id != null ? "Ver detalle" : undefined}
                  >
                    {columns.map((column) => (
                      <td key={column.key} style={tdStyle}>
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div style={{ ...footerRowStyle, marginTop: "12px" }}>
          <span style={{ color: "var(--text-secondary)", fontSize: "13px" }}>
            {total.toLocaleString("es-AR")} envío{total === 1 ? "" : "s"} · página {page} de {totalPages}
          </span>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              style={page <= 1 ? pagerButtonDisabledStyle : pagerButtonStyle}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Anterior
            </button>
            <button
              type="button"
              style={page >= totalPages ? pagerButtonDisabledStyle : pagerButtonStyle}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Siguiente →
            </button>
          </div>
        </div>
      </Card>

      <EnvioLegacyDetailModal
        open={selectedId != null}
        lightdataId={selectedId}
        token={token}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
